import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { addAlpha } from '@algoan/deep-dive/theme';
import { messages, categoryGroupMessages } from './cash-flow-modal-messages';
import { TransactionCategory, AutocompleteInputType } from '@algoan/dashboard/model';
import { ArrowIcon } from '../icons';
import { useTransactionCategoryTranslator } from '../../messages';
import { CategoryDropdownOption, CategoryGroup } from './categories-list';

interface ListItemSelectProps {
  categoriesList: CategoryDropdownOption[];
  handleChangeCategory(categorySelected: TransactionCategory): void;
  dropDownHeader: string;
  oldCategory?: TransactionCategory;
  currentCategory?: TransactionCategory;
}

export const ListItemSelect: React.FC<ListItemSelectProps> = ({
  categoriesList,
  handleChangeCategory,
  dropDownHeader,
  oldCategory,
  currentCategory,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const translateTransactionCategory = useTransactionCategoryTranslator();
  const selectedCategoryGroupList = Object.values(CategoryGroup).map((categoryGroup) => ({
    categoryGroup,
    subcategory: categoriesList.filter((category) => category.categoryGroup === categoryGroup),
  }));

  return (
    <S.DropdownList type={AutocompleteInputType.SELECT}>
      {oldCategory && (
        <>
          <S.ListItemInformation>{intl.formatMessage(messages.oldCategory)}</S.ListItemInformation>
          <S.ListItem type={AutocompleteInputType.AUTOCOMPLETE} onClick={() => handleChangeCategory(oldCategory)}>
            <S.ListItemCategory type={AutocompleteInputType.AUTOCOMPLETE}>
              <span>{translateTransactionCategory(oldCategory)}</span>
            </S.ListItemCategory>
          </S.ListItem>
        </>
      )}
      <S.ListItemInformation>{dropDownHeader}</S.ListItemInformation>
      {selectedCategoryGroupList.map(
        (category, index) =>
          category.subcategory.length > 0 && (
            <S.ListItem type={AutocompleteInputType.SELECT} key={`${category.subcategory}-${index}`}>
              <S.ListItemCategory
                type={AutocompleteInputType.SELECT}
                data-testid={`recategorization-dropdown-category-group-${category.categoryGroup}`}
              >
                <span>{intl.formatMessage(categoryGroupMessages[category.categoryGroup])}</span>
                <ArrowIcon color={theme.secondary.softBlue1} direction="right" />
              </S.ListItemCategory>
              <S.SubCategoryContainer>
                <S.SubCategoryShadow>
                  {category.subcategory.map((element) => (
                    <S.SubCategoryItem
                      data-testid={'recategorization-dropdown-category-item'}
                      key={`${element.categoryGroup}-${element.category}`}
                      onClick={() => handleChangeCategory(element.category)}
                    >
                      <S.Category
                        isCurrent={currentCategory === element.category}
                        data-testid={`recategorization-dropdown-category-${element.category}`}
                      >
                        {translateTransactionCategory(element.category)}
                      </S.Category>
                    </S.SubCategoryItem>
                  ))}
                </S.SubCategoryShadow>
              </S.SubCategoryContainer>
            </S.ListItem>
          ),
      )}
    </S.DropdownList>
  );
};

const S = {
  DropdownList: styled.ul<{ type: AutocompleteInputType }>`
    ${(props) => props.type === AutocompleteInputType.AUTOCOMPLETE && 'max-height: 55vh; overflow-y: auto;'};
    padding: 0;
    margin-top: 8px;
    background: #ffffff;
    box-shadow: 0 1px 15px ${(props) => addAlpha(props.theme.algoanBlue, 0.15)};
    color: ${(props) => props.theme.algoanBlue};
    font-weight: 400;
    border-radius: 4px;
    font-size: 14px;
    & > li:hover > ul {
      display: block;
    }
  `,
  ListItemInformation: styled.li`
    display: flex;
    list-style: none;
    align-items: center;
    font-size: 14px;
    height: 40px;
    padding-left: 16px;
    color: ${(props) => props.theme.secondary.softBlue2};
    background: ${(props) => props.theme.secondary.chalk};
  `,
  ListItem: styled.li<{ type: AutocompleteInputType }>`
    ${(props) => (props.type === AutocompleteInputType.SELECT ? 'height: 40px' : 'min-height: 40px')};
    display: flex;
    border-bottom: 1px solid ${(props) => props.theme.iceBlue};
    list-style: none;
    align-items: center;
    font-size: 15px;
    padding-left: 24px;
    &:hover {
      background: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
      cursor: pointer;
    }
    &:last-child {
      border: none;
    }
  `,
  ListItemCategory: styled.div<{ type: AutocompleteInputType }>`
    ${(props) => (props.type === AutocompleteInputType.SELECT ? 'min-width: 309px' : 'width: 100%')};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-right: 17px;
    padding-top: 7px;
    padding-bottom: 7px;
    & > :first-child {
      max-width: ${(props) => (props.type === AutocompleteInputType.SELECT ? '50' : '47')}%;
      text-align: left;
    }
    & > :last-child {
      max-width: 100%;
      text-align: right;
    }
  `,
  Category: styled.span<{ isCurrent: boolean }>`
    font-weight: ${({ isCurrent }) => (isCurrent ? 'bold' : 'normal')};
  `,
  SubCategoryContainer: styled.ul`
    display: none;
    padding: 0;
    background: #ffffff;
    box-shadow: 0 1px 15px ${(props) => addAlpha(props.theme.algoanBlue, 0.15)};
    font-weight: 400;
    border-radius: 4px;
    font-size: 14px;
    /* Works on Chrome/Edge/Safari */
    *::-webkit-scrollbar {
      width: 10px;
    }
    *::-webkit-scrollbar-track {
      background: white;
    }
    *::-webkit-scrollbar-thumb {
      background-color: lightgrey;
      border-radius: 6px;
    }
  `,
  SubCategoryShadow: styled.div`
    min-width: 316px;
    max-height: 60vh;
    overflow: auto;
    border-radius: 4px;
    background:
      linear-gradient(white 30%, rgba(255, 255, 255, 0)),
      linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
      linear-gradient(rgba(0, 70, 107, 0.1), rgba(0, 0, 0, 0)),
      linear-gradient(rgba(0, 0, 0, 0), rgba(0, 70, 107, 0.1)) 0 100%;
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 10px,
      100% 10px;
    background-attachment: local, local, scroll, scroll;
  `,
  SubCategoryItem: styled.li`
    display: flex;
    list-style: none;
    align-items: center;
    min-height: 40px;
    padding: 0 16px;
    &:hover {
      background: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
      cursor: pointer;
    }
    & span {
      padding: 10px 0;
    }
  `,
};
